import { useState, useEffect, useCallback } from "react";
import { Link2, Plus, Play, Square, Trash2, Copy, ExternalLink, Users, QrCode, X, Settings, Languages, Zap } from "lucide-react";
import { toast } from "sonner";
import QRCode from "react-qr-code";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PresenterLanguagesSelector, AVAILABLE_LANGUAGES } from "./PresenterLanguagesSelector";
import { useLanguage } from "@/i18n/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function PermanentRoomCard({ 
  isPro,
  userId,
  sourceLang, 
  targetLang,
  currentTranslation,
  originalText,
  detectedSourceLang, // Actual language detected by Whisper
  onRoomActivated,  // Callback when a permanent room is activated
  onPresenterLanguagesChange  // Callback to notify parent when room is active with languages
}) {
  const { t } = useLanguage();
  const [permanentRooms, setPermanentRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [newRoomName, setNewRoomName] = useState("");
  const [newRoomSlug, setNewRoomSlug] = useState("");
  const [newRoomLanguages, setNewRoomLanguages] = useState([sourceLang || "pt"]);

  // Get the base URL for sharing
  const getShareUrl = useCallback((slug) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/sala/${slug}`;
  }, []);

  // Load user's permanent rooms
  const loadPermanentRooms = useCallback(async () => {
    if (!userId) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/rooms/permanent/my-rooms?user_id=${userId}`);
      const data = await response.json();
      if (data.success) {
        setPermanentRooms(data.rooms || []);
        // Check if any room is currently live
        const liveRoom = data.rooms?.find(r => r.is_session_live);
        if (liveRoom) {
          setActiveRoom(liveRoom);
          // Restore Whisper language hints after a page refresh
          if (onPresenterLanguagesChange && liveRoom.presenter_languages?.length) {
            onPresenterLanguagesChange(liveRoom.presenter_languages);
          }
        }
      }
    } catch (e) {
      console.error("[PERMANENT_ROOM] Error loading rooms:", e);
    }
  }, [userId, onPresenterLanguagesChange]);

  // Load rooms on mount
  useEffect(() => {
    if (isPro && userId) {
      loadPermanentRooms();
    }
  }, [isPro, userId, loadPermanentRooms]);

  // Create new permanent room
  const createPermanentRoom = async () => {
    if (!newRoomName.trim()) {
      toast.error("Digite um nome para a sala");
      return;
    }
    
    if (newRoomLanguages.length === 0) {
      toast.error("Selecione pelo menos um idioma dos apresentadores");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/rooms/permanent/create?user_id=${userId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newRoomName.trim(),
          slug: newRoomSlug.trim() || null,
          source_lang: sourceLang,
          target_lang: targetLang,
          presenter_languages: newRoomLanguages
        })
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success(`Link fixo "${data.room.name}" criado!`);
        setShowCreateModal(false);
        setNewRoomName("");
        setNewRoomSlug("");
        setNewRoomLanguages([sourceLang || "pt"]);
        loadPermanentRooms();
      } else {
        toast.error(data.error || "Erro ao criar link fixo");
      }
    } catch (e) {
      toast.error("Erro ao criar link fixo");
    } finally {
      setIsLoading(false);
    }
  };

  // Start session for a permanent room
  const startSession = async (slug) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/rooms/permanent/${slug}/start`, {
        method: "POST"
      });
      const data = await response.json();
      if (data.success) {
        toast.success("Sessão iniciada!");
        setActiveRoom(data.room);
        if (onRoomActivated) {
          onRoomActivated(data.room);
        }
        // Notify parent about presenter languages
        const roomData = permanentRooms.find(r => r.slug === slug);
        if (onPresenterLanguagesChange && roomData?.presenter_languages) {
          onPresenterLanguagesChange(roomData.presenter_languages);
        }
        loadPermanentRooms();
      } else {
        toast.error(data.error || "Erro ao iniciar sessão");
      }
    } catch (e) {
      toast.error("Erro ao iniciar sessão");
    } finally {
      setIsLoading(false);
    }
  };

  // Stop session for a permanent room
  const stopSession = async (slug) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/rooms/permanent/${slug}/stop`, {
        method: "POST"
      });
      const data = await response.json();
      if (data.success) {
        toast.success("Sessão encerrada!");
        setActiveRoom(null);
        // Clear presenter languages
        if (onPresenterLanguagesChange) {
          onPresenterLanguagesChange([]);
        }
        loadPermanentRooms();
      } else {
        toast.error(data.error || "Erro ao encerrar sessão");
      }
    } catch (e) {
      toast.error("Erro ao encerrar sessão");
    } finally {
      setIsLoading(false);
    }
  };

  // Delete permanent room
  const deleteRoom = async (slug) => {
    if (!window.confirm("Tem certeza que deseja excluir este link fixo permanentemente?")) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/rooms/permanent/${slug}`, {
        method: "DELETE"
      });
      const data = await response.json();
      if (data.success) {
        toast.success("Link fixo excluído!");
        if (activeRoom?.slug === slug) {
          setActiveRoom(null);
        }
        loadPermanentRooms();
      } else {
        toast.error(data.error || "Erro ao excluir");
      }
    } catch (e) {
      toast.error("Erro ao excluir link fixo");
    }
  };

  // Copy link to clipboard
  const copyLink = (slug) => {
    navigator.clipboard.writeText(getShareUrl(slug));
    toast.success("Link copiado!");
  };

  // Send translation to active room via HTTP
  useEffect(() => {
    if (!activeRoom || !currentTranslation) return;
    
    // Use detected language if available, otherwise fall back to configured sourceLang
    const actualSourceLang = detectedSourceLang || sourceLang;
    
    const sendTranslation = async () => {
      try {
        await fetch(`${BACKEND_URL}/api/rooms/${activeRoom.slug || activeRoom.room_id}/translation`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            text: currentTranslation,
            original: originalText || currentTranslation,  // Send original for per-viewer translation
            source_lang: actualSourceLang,  // Use detected language, not configured
            target_lang: targetLang
          })
        });
        console.log("[PERMANENT_ROOM] Translation sent with source_lang:", actualSourceLang);
      } catch (e) {
        console.error("[PERMANENT_ROOM] Error sending translation:", e);
      }
    };
    
    sendTranslation();
  }, [currentTranslation, activeRoom, sourceLang, targetLang, detectedSourceLang, originalText]);

  // Non-PRO view
  if (!isPro) {
    return (
      <Card className="col-span-12 bento-card opacity-60" data-testid="permanent-room-card">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-white font-['Outfit']">
            <Link2 className="w-5 h-5" />
            {t("permanentLinks")}
            <span className="ml-2 text-xs font-normal px-2 py-0.5 rounded bg-yellow-500/20 text-yellow-400">
              PRO
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-zinc-400 text-sm mb-3">
            {t("permanentLinkDescription")}
          </p>
          <Link 
            to="/pricing" 
            className="block w-full text-center py-2.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {t("upgradePro")}
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="col-span-12 bento-card" data-testid="permanent-room-card">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center justify-between text-white font-['Outfit']">
            <div className="flex items-center gap-2">
              <Link2 className="w-5 h-5" />
              {t("permanentLinks")}
              <span className="ml-2 text-xs font-normal px-2 py-0.5 rounded bg-green-500/20 text-green-400">
                {t("permanent")}
              </span>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
              title={t("createPermanentLink")}
            >
              <Plus className="w-5 h-5" />
            </button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {permanentRooms.length === 0 ? (
            <div className="text-center py-6">
              <Link2 className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-zinc-400 text-sm mb-4">
                {t("noPermanentLinksYet")}
              </p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                {t("createPermanentLink")}
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {permanentRooms.map((room) => (
                <div 
                  key={room.slug}
                  className={`p-4 rounded-lg border ${
                    room.is_session_live 
                      ? 'bg-green-900/20 border-green-500/30' 
                      : 'bg-zinc-800/50 border-zinc-700'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h4 className="text-white font-medium">{room.name}</h4>
                      <p className="text-xs text-gray-400 font-mono">{room.slug}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {room.is_session_live && (
                        <span className="flex items-center gap-1 text-xs text-green-400 bg-green-500/20 px-2 py-1 rounded">
                          <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                          Ao vivo
                        </span>
                      )}
                      <span className="flex items-center gap-1 text-xs text-gray-400">
                        <Users className="w-3 h-3" />
                        {room.viewer_count || 0}
                      </span>
                    </div>
                  </div>
                  
                  {/* Show presenter languages if available */}
                  {room.presenter_languages && room.presenter_languages.length > 0 && (
                    <div className="flex items-center gap-1 mb-2">
                      <Languages className="w-3 h-3 text-gray-500" />
                      <span className="text-xs text-gray-500">
                        Idiomas: {room.presenter_languages.map(code => {
                          const lang = AVAILABLE_LANGUAGES.find(l => l.code === code);
                          return lang ? lang.nativeName : code.toUpperCase();
                        }).join(", ")}
                      </span>
                    </div>
                  )}
                  
                  <div className="flex flex-wrap gap-2">
                    {room.is_session_live ? (
                      <button
                        onClick={() => stopSession(room.slug)}
                        disabled={isLoading}
                        className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-colors inline-flex items-center gap-1"
                      >
                        <Square className="w-3 h-3" />
                        Encerrar
                      </button>
                    ) : (
                      <button
                        onClick={() => startSession(room.slug)}
                        disabled={isLoading}
                        className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors inline-flex items-center gap-1"
                      >
                        <Play className="w-3 h-3" />
                        Iniciar
                      </button>
                    )}
                    
                    <button
                      onClick={() => {
                        setActiveRoom(room);
                        setShowQRModal(true);
                      }}
                      className="px-3 py-1.5 bg-zinc-700 hover:bg-zinc-600 text-white text-sm rounded-lg transition-colors inline-flex items-center gap-1"
                    >
                      <QrCode className="w-3 h-3" />
                      QR Code
                    </button>
                    
                    <button
                      onClick={() => copyLink(room.slug)}
                      className="px-3 py-1.5 bg-zinc-700 hover:bg-zinc-600 text-white text-sm rounded-lg transition-colors inline-flex items-center gap-1"
                    >
                      <Copy className="w-3 h-3" />
                      Copiar
                    </button>
                    
                    <button
                      onClick={() => deleteRoom(room.slug)}
                      className="px-3 py-1.5 bg-zinc-700 hover:bg-zinc-600 text-red-400 hover:text-red-300 text-sm rounded-lg transition-colors inline-flex items-center gap-1"
                      title="Excluir link fixo"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowCreateModal(false)}>
          <div className="bg-gray-800 rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">{t("createPermanentLink")}</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">{t("roomName")} *</label>
                <input
                  type="text"
                  value={newRoomName}
                  onChange={(e) => setNewRoomName(e.target.value)}
                  placeholder="Ex: Church Central - Sunday Service"
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">{t("customCode")} ({t("customCodeOptional")})</label>
                <input
                  type="text"
                  value={newRoomSlug}
                  onChange={(e) => setNewRoomSlug(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))}
                  placeholder="Ex: CHURCH-CENTRAL"
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 font-mono"
                  maxLength={20}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t("leaveEmptyToGenerate")}
                </p>
              </div>
              
              {/* Presenter Languages Selector */}
              <PresenterLanguagesSelector
                selectedLanguages={newRoomLanguages}
                onChange={setNewRoomLanguages}
                label={t("presenterLanguages")}
                description={t("presenterLanguagesDescription")}
              />
              
              <div className="bg-gray-900 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">{t("linkPreview")}:</p>
                <p className="text-sm text-blue-400 font-mono break-all">
                  {window.location.origin}/sala/{newRoomSlug || 'CODE-XXXX'}
                </p>
              </div>
              
              <button
                onClick={createPermanentRoom}
                disabled={isLoading || !newRoomName.trim() || newRoomLanguages.length === 0}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Criando...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Criar Link Fixo
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQRModal && activeRoom && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowQRModal(false)}>
          <div className="bg-gray-800 rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">{t("qrCodeFixedLink")}</h2>
              <button
                onClick={() => setShowQRModal(false)}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="text-center mb-4">
              <p className="text-gray-400 mb-1">{activeRoom.name}</p>
              <p className="text-2xl font-mono font-bold text-blue-400">
                {activeRoom.slug}
              </p>
            </div>
            
            {/* QR Code */}
            <div className="bg-white p-4 rounded-xl mb-4 flex items-center justify-center">
              <QRCode 
                value={getShareUrl(activeRoom.slug)} 
                size={200}
                level="M"
              />
            </div>
            
            {/* URL */}
            <div className="bg-gray-900 rounded-lg p-3 mb-4">
              <p className="text-xs text-gray-500 mb-1">{t("permanentLinkLabel")}</p>
              <p className="text-sm text-blue-400 break-all font-mono">
                {getShareUrl(activeRoom.slug)}
              </p>
            </div>
            
            <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-3 mb-4">
              <p className="text-sm text-green-400">
                {t("qrNeverChanges")}
              </p>
            </div>
            
            {/* Action buttons */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => copyLink(activeRoom.slug)}
                className="py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <Copy className="w-4 h-4" />
                {t("copyLink")}
              </button>
              
              <a
                href={getShareUrl(activeRoom.slug)}
                target="_blank"
                rel="noopener noreferrer"
                className="py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                {t("openLink")}
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
